import type { ReactElement } from "react";
import type { VideoClientMetadata, VideoValidationResult } from "../../hooks/useVideoUpload";

interface VideoValidationWarningsProps {
  metadata: VideoClientMetadata | null;
  validation: VideoValidationResult | null;
}

/**
 * Format duration in human-readable format
 */
function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
}

/**
 * Video Validation Warnings Component
 * Displays extracted metadata and any validation warnings (non-blocking)
 * Phase 5B: Client-Side Video Validation
 */
export function VideoValidationWarnings({
  metadata,
  validation,
}: Readonly<VideoValidationWarningsProps>): ReactElement | null {
  // Don't render if no metadata extracted
  if (!metadata) {
    return null;
  }

  const hasWarnings = validation && validation.warnings.length > 0;

  return (
    <div className="mt-3 space-y-2">
      {/* Extracted Metadata Display */}
      <div className="flex flex-wrap gap-3 text-xs text-neutral-400">
        <span className="flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
            />
          </svg>
          {metadata.width}×{metadata.height}
        </span>
        <span className="flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {formatDuration(metadata.duration)}
        </span>
        <span className="flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
            />
          </svg>
          {metadata.aspectRatio}
        </span>
      </div>

      {/* Validation Warnings (non-blocking) */}
      {hasWarnings && (
        <div className="p-3 bg-yellow-900/20 border border-yellow-700/30 rounded-md">
          <p className="text-sm text-yellow-400 font-medium mb-1 flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            Video Quality Warnings
          </p>
          <ul className="text-xs text-yellow-300/90 space-y-1 list-disc list-inside">
            {validation.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
          <p className="text-xs text-yellow-500/70 mt-2">
            These are recommendations only. You can still upload this video.
          </p>
        </div>
      )}

      {/* Validation Passed */}
      {validation && validation.valid && validation.warnings.length === 0 && (
        <div className="flex items-center gap-1.5 text-xs text-green-400">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Video meets quality requirements
        </div>
      )}
    </div>
  );
}
